/**
 * The player's board: what they have marked, and what is wrong with it.
 *
 * Checking is done against the *rules*, never against the stored solution. The
 * puzzle has exactly one solution, so the two would agree on a finished board —
 * but only the rules can say anything useful about an unfinished one, which is
 * the whole point of checking as you go. It also means a star sitting where the
 * solution has none is not flagged until it actually breaks something, so the
 * board never quietly tells the player the answer.
 */

import { NEIGHBORS, buildGeometry } from './board';
import { Deducer, type DeductionShape, type NextDeduction } from './solver';
import { CELLS, DOT, EMPTY, SIZE, STAR, STARS, type Mark, type Puzzle } from './types';

export type Unit = { kind: 'row' | 'column' | 'region'; index: number };

export interface BoardStatus {
  /** Cells drawn as broken: a star in a genuine rule violation. */
  errors: Set<number>;
  /** Units the player has crossed out so heavily they can no longer be filled. */
  starved: Unit[];
  solved: boolean;
  placed: number;
}

export function emptyMarks(): Mark[] {
  return new Array<Mark>(CELLS).fill(EMPTY);
}

/**
 * Advances one cell through empty → ✕ → star → empty.
 *
 * The ✕ comes first because that is what a player does most: the cross is a note
 * about where a star *cannot* go, and a board is mostly crosses.
 */
export function cycleMark(mark: Mark): Mark {
  if (mark === EMPTY) return DOT;
  if (mark === DOT) return STAR;
  return EMPTY;
}

/**
 * What a drag does to one cell, or null to leave it alone.
 *
 * The square the drag started on chooses which of the two it is, the way a
 * paint tool picks up a colour: from a blank square it lays ✕ down the line,
 * from a ✕ it rubs them out. Both are the same gesture over the same run of
 * squares — crossing out a row and changing your mind about it should not be
 * one flick of the finger and then ten taps.
 *
 * Neither direction touches a star. A ✕ is a note about where a star cannot go
 * and costs nothing to redo; a star is a commitment, and a finger sliding past
 * one on its way somewhere else must not take it off the board. Only a tap can
 * do that.
 */
export function dragMark(origin: Mark, mark: Mark): Mark | null {
  if (origin === DOT) return mark === DOT ? EMPTY : null;
  return mark === EMPTY ? DOT : null;
}

// ── The player's board, and where its crosses came from ──────────────────────

/**
 * The marks, plus the provenance of the ones this code placed by itself.
 *
 * With auto-crossing on, putting a star down also crosses out the eight cells
 * around it. Taking the star back has to undo that — but only that. A ✕ the
 * player made themselves means something to them and must survive, and the two
 * are indistinguishable once written into `marks`, so each automatic ✕ records
 * the stars that asked for it.
 *
 * `sources[cell]` is empty for every cell the player owns: the untouched ones,
 * the crosses they placed by hand or with "fill", and the stars. A cell with a
 * non-empty list holds a ✕ that exists only because those stars do, and it goes
 * when the last of them does. A list, not a count, because the answer to "did
 * *this* star put it there" is what removal needs and a count cannot give.
 */
export interface Board {
  marks: Mark[];
  /** For each cell holding an automatic ✕, the stars that placed it. */
  sources: number[][];
}

export interface Change {
  cell: number;
  mark: Mark;
}

export function emptyBoard(): Board {
  return { marks: emptyMarks(), sources: Array.from({ length: CELLS }, () => []) };
}

/**
 * Writes changes into the board, maintaining the automatic crosses around stars.
 *
 * Returns the board unchanged — the same object — when nothing moves, so the
 * caller can use identity to decide whether this is worth an undo entry.
 *
 * `autoCross` only governs stars being *placed*. A star being removed always
 * takes its own crosses with it, whether or not the setting is still on: those
 * crosses are on the board because that star was placed, and leaving them
 * stranded when the setting is switched off mid-puzzle would strand them for
 * good, with nothing left that knows they were ever automatic.
 */
export function applyMarks(board: Board, changes: readonly Change[], autoCross: boolean): Board {
  const marks = board.marks.slice();
  const sources = board.sources.slice();
  let touched = false;

  /** Withdraws one star's claim on the cells around it. */
  const retract = (star: number): void => {
    for (const neighbor of NEIGHBORS[star] as number[]) {
      const claims = sources[neighbor] as number[];
      if (!claims.includes(star)) continue;
      const rest = claims.filter((claim) => claim !== star);
      sources[neighbor] = rest;
      // Nothing wants this ✕ any more, so it goes. It cannot be a cross the
      // player cares about: touching a cell by hand clears its provenance, so
      // only a ✕ that has been automatic all along still has claims on it.
      if (rest.length === 0 && marks[neighbor] === DOT) marks[neighbor] = EMPTY;
    }
  };

  for (const change of changes) {
    const before = marks[change.cell] as Mark;
    if (before === change.mark) continue;
    marks[change.cell] = change.mark;
    touched = true;

    // The player has just decided this cell for themselves. Whatever the stars
    // around it wanted, it is theirs now.
    if ((sources[change.cell] as number[]).length > 0) sources[change.cell] = [];

    if (before === STAR) retract(change.cell);
    if (change.mark !== STAR || !autoCross) continue;

    for (const neighbor of NEIGHBORS[change.cell] as number[]) {
      const claims = sources[neighbor] as number[];
      if (marks[neighbor] === EMPTY) {
        marks[neighbor] = DOT;
        sources[neighbor] = [...claims, change.cell];
      } else if (marks[neighbor] === DOT && claims.length > 0) {
        // Already crossed for another star. Both stars now hold it, so it
        // survives until both are gone.
        sources[neighbor] = [...claims, change.cell];
      }
      // A ✕ the player placed themselves is left alone and never claimed —
      // which is the whole point of tracking this — as is a neighbouring star,
      // wrong as it is, since crossing it out would erase a move of theirs.
    }
  }

  return touched ? { marks, sources } : board;
}

export function analyseBoard(puzzle: Puzzle, marks: readonly Mark[]): BoardStatus {
  const { regions } = puzzle;
  const errors = new Set<number>();

  const starCounts = [0, 1, 2].map(() => new Array<number>(SIZE).fill(0));
  const room = [0, 1, 2].map(() => new Array<number>(SIZE).fill(0));
  // Stars grouped by unit, so an over-filled unit can flag all of them at once.
  const starsIn: number[][] = Array.from({ length: SIZE * 3 }, () => []);

  let placed = 0;
  for (let cell = 0; cell < CELLS; cell += 1) {
    const indices = [Math.floor(cell / SIZE), cell % SIZE, regions[cell] as number];
    const mark = marks[cell] as Mark;
    if (mark === STAR) {
      placed += 1;
      indices.forEach((index, family) => {
        const counts = starCounts[family] as number[];
        counts[index] = (counts[index] as number) + 1;
        (starsIn[family * SIZE + index] as number[]).push(cell);
      });
    }
    if (mark !== DOT) {
      // A cell that could still take a star: empty, or already holding one.
      indices.forEach((index, family) => {
        const free = room[family] as number[];
        free[index] = (free[index] as number) + 1;
      });
    }
  }

  // Touching stars — both are at fault, so both are flagged.
  for (let cell = 0; cell < CELLS; cell += 1) {
    if (marks[cell] !== STAR) continue;
    for (const neighbor of NEIGHBORS[cell] as number[]) {
      if (marks[neighbor] === STAR) {
        errors.add(cell);
        errors.add(neighbor);
      }
    }
  }

  // Over-filled units: every star in one is suspect, since which is the spare is
  // exactly what the player has not decided yet.
  for (const cells of starsIn) {
    if (cells.length > STARS) for (const cell of cells) errors.add(cell);
  }

  const kinds: Unit['kind'][] = ['row', 'column', 'region'];
  const starved: Unit[] = [];
  kinds.forEach((kind, family) => {
    const free = room[family] as number[];
    for (let index = 0; index < SIZE; index += 1) {
      if ((free[index] as number) < STARS) starved.push({ kind, index });
    }
  });

  const solved =
    errors.size === 0 &&
    starved.length === 0 &&
    starCounts.every((counts) => counts.every((count) => count === STARS));

  return { errors, starved, solved, placed };
}

// ── Hints ───────────────────────────────────────────────────────────────────

export interface Hint {
  message: string;
  /** Cells to highlight. A whole unit, so it points without answering. */
  cells: number[];
}

/** Units are laid out rows, then columns, then regions — as in the solver. */
function familyOf(unit: number): 'row' | 'column' | 'region' {
  if (unit < SIZE) return 'row';
  if (unit < SIZE * 2) return 'column';
  return 'region';
}

/**
 * How a unit is named in a sentence.
 *
 * Rows and columns have numbers and are called by them. Regions have none, so a
 * region is only ever named when it is lit up on the board — "the highlighted
 * region" — and a hint that needs to mention one that is not says what it is
 * instead ("a region") rather than send the player hunting for it.
 */
function unitName(unit: number, lit: ReadonlySet<number>): string {
  const family = familyOf(unit);
  if (family === 'row') return `row ${unit + 1}`;
  if (family === 'column') return `column ${unit - SIZE + 1}`;
  return lit.has(unit) ? 'the highlighted region' : 'a region';
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "rows 3 and 4", "columns 2, 5 and 8", "the two highlighted regions". */
function unitList(units: readonly number[], lit: ReadonlySet<number>): string {
  const family = familyOf(units[0] as number);
  if (units.length === 1) return unitName(units[0] as number, lit);
  if (family === 'region') {
    const count = units.length === 2 ? 'two' : units.length === 3 ? 'three' : `${units.length}`;
    return units.every((unit) => lit.has(unit))
      ? `the ${count} highlighted regions`
      : `${count} regions`;
  }
  const numbers = units.map((unit) => (family === 'row' ? unit + 1 : unit - SIZE + 1));
  const listed = `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`;
  return `${family}s ${listed}`;
}

function stars(count: number): string {
  if (count === 1) return 'one star';
  if (count === 2) return 'two stars';
  return `${count} stars`;
}

/**
 * The one-square payoff, as its own sentence after the explanation.
 *
 * Said outright which mark is coming, because crossing a square out and
 * committing a star to it are different moves with different costs if the hint
 * has been misread, and the player should know which is waiting before they go
 * looking. The other shapes need no sentence of their own: the explanation
 * already says what to do.
 */
const PAYOFF: Record<DeductionShape, string> = {
  completes: ' That finishes it.',
  'one-cross': ' Exactly one square comes of it, and it can be crossed out.',
  'one-star': ' Exactly one square comes of it, and it has to be a star.',
  plain: '',
};

/**
 * What the cells a hint lights up are: the units its sentence talks about.
 *
 * Always whole units, never the squares the deduction settles, which would be
 * the answer. Usually that is one unit; the two rungs that reason *between*
 * units light up the ones they reason from — the line and the region that
 * overlap, the rows taken together — because "take these together" is not
 * something a player can do without seeing which. A trial lights the row to try
 * a square in and nothing else: what it runs into is theirs to find.
 */
function litUnits(next: NextDeduction): number[] {
  const { detail } = next;
  if (detail.kind === 'overlap') return [detail.from, next.unit];
  if (detail.kind === 'group') return detail.members;
  return [next.unit];
}

/**
 * The hint's sentences, in the voice of someone teaching the game.
 *
 * Each says what to look at, what is true of it, and why that forces something
 * — the reasoning, because that is what a stuck player is missing, and a
 * reason they have seen once is a technique they can use again. What it never
 * says is which square: the player still has to find it, which is the part
 * worth doing.
 */
function teach(next: NextDeduction, lit: ReadonlySet<number>): string {
  const { detail, approach } = next;
  const unit = unitName(next.unit, lit);
  const Unit = capitalise(unit);
  const payoff = PAYOFF[next.shape];

  switch (detail.kind) {
    case 'count': {
      if (detail.need === 0) {
        const rest =
          next.shape === 'one-cross'
            ? 'so the one open square it has left cannot hold another.'
            : 'so none of its other squares can hold one — they can all be crossed out.';
        return `${Unit} already has both its stars, ${rest}${payoff}`;
      }
      if (detail.need === 1) {
        return (
          `${Unit} still needs one star, and only one of its squares is left open — ` +
          'so that square has to be the star.' +
          payoff
        );
      }
      return (
        `${Unit} still needs ${stars(detail.need)}, and it has exactly ${detail.open} open ` +
        'squares left, so there is no room to be choosy: every one of them has to be a star.' +
        payoff
      );
    }
    case 'overlap': {
      const from = unitName(detail.from, lit);
      const one = detail.need === 1;
      return (
        `${capitalise(from)} needs ${stars(detail.need)} more, and every open square it has ` +
        `left lies inside ${unit}. ${Unit} needs ${stars(detail.need)} too, so ` +
        (one
          ? `its star has to be the one ${from} puts there`
          : `its stars have to be the ones ${from} puts there`) +
        ` — any square of ${unit} outside ${from} can be crossed out.` +
        payoff
      );
    }
    case 'group': {
      const members = unitList(detail.members, lit);
      const kind = familyOf(detail.members[0] as number);
      const targetKind = familyOf(detail.targets[0] as number);
      const single = detail.targets.length === 1;
      const targets =
        targetKind === 'region'
          ? `just ${single ? 'one region' : `${detail.targets.length} regions`}`
          : unitList(detail.targets, lit);
      const these = detail.members.length === 1 ? `that ${kind}` : `these ${kind}s`;
      const matching = single
        ? `which needs exactly ${stars(detail.need)} itself`
        : `which need exactly ${stars(detail.need)} between them as well`;
      const those = single
        ? `that ${targetKind} gets all its`
        : `those ${targetKind}s get all their`;
      const theirs = single ? 'its' : 'their';
      return (
        `Take ${members} together. Between them they need ${stars(detail.need)}, and every ` +
        `open square they have lies in ${targets}, ${matching}. So ${those} stars from ${these}, ` +
        `and any of ${theirs} squares outside ${these} can be crossed out.` +
        payoff
      );
    }
    case 'fit': {
      const room = `${stars(detail.need)} to place among ${detail.open} open squares`;
      if (approach === 'only-fit') {
        return (
          `${Unit} is small enough to take in at a glance: ${room}, and stars may not touch, ` +
          'so there is only one arrangement that fits. Put them in and cross out the rest.' +
          payoff
        );
      }
      if (approach === 'shared-square') {
        // The explanation already says a star goes in, so no payoff sentence.
        return (
          `${Unit} has ${room}. Sketch two or three arrangements that fit and you will see ` +
          'they all use the same square — so whatever happens to the rest, that square is a star.' +
          (next.shape === 'one-star' ? '' : payoff)
        );
      }
      // 'no-room': a square the unit's stars cannot do without, one they would
      // be left stuck by, or one just outside that they touch wherever they go.
      if (next.shape === 'one-star' || (next.shape === 'plain' && !next.outside)) {
        return (
          `${Unit} has ${room}. Look for the square it cannot do without: imagine it crossed ` +
          `out, and see that what is left of ${unit} can no longer fit its stars.` +
          payoff
        );
      }
      if (next.outside) {
        const where = detail.need === 1 ? 'Wherever its star goes' : 'However its stars sit';
        const touch = detail.need === 1 ? 'it touches' : 'they touch';
        if (next.shape === 'one-cross') {
          return (
            `${Unit} has ${room}. ${where}, ${touch} one particular square just outside it — ` +
            'so that square cannot hold a star, and can be crossed out now.'
          );
        }
        return (
          `${Unit} has ${room}. ${where}, ${touch} the same few squares just outside it — ` +
          'so none of those can hold a star, and they can be crossed out now.'
        );
      }
      return (
        `${Unit} has ${room}. Look for a square that would leave it stuck: imagine a star ` +
        `there, cross out the squares around it, and see whether ${unit} still has room for ` +
        'the rest of its stars. If it does not, that square can be crossed out.' +
        payoff
      );
    }
    case 'trial': {
      const supposed = detail.star ? 'holds a star' : 'is crossed out';
      const opposite = detail.star ? 'that square is crossed out' : 'that square is a star';
      const breaks =
        detail.breaks === null
          ? 'a row, column or region ends up without room for its stars'
          : `${unitName(detail.breaks, lit)} ends up without room for its stars`;
      return (
        'Nothing simple is left, so it is time to test an idea. Pick an open square in ' +
        `${unit} and suppose it ${supposed}. Follow what that forces — crosses around each ` +
        `star, lines that fill up — and for the right square, ${breaks}. When a guess leads ` +
        `somewhere impossible, the opposite has to be true: ${opposite}.`
      );
    }
  }
}

/**
 * Where to look next, and why.
 *
 * Reasoning runs from the stars the player has placed, never their crosses: a
 * cross is a belief and can be wrong, while a star is a commitment. Their
 * crosses do come in as *what they already know*, so the hint skips anything the
 * solver merely re-derives from marks they made themselves — otherwise the first
 * thing it finds is almost always the ring of crosses around their own stars.
 *
 * The hint names the rows, columns or regions the reasoning is about and walks
 * through it — never which cell, which would just be the answer. Among the
 * deductions it could offer it prefers one square that can be crossed out, then
 * a region it can finish outright, then one square that has to be a star, and
 * only then the rest; see `byUsefulness` in solver.ts.
 *
 * A mark in the wrong place makes every deduction after it meaningless, so both
 * kinds are checked first, and they are the one place a hint consults the
 * solution. The automatic checker cannot flag either: a wrong star that breaks
 * no rule yet is not an error, and saying so unprompted would give the game away.
 */
export function nextHint(puzzle: Puzzle, marks: readonly Mark[]): Hint {
  const status = analyseBoard(puzzle, marks);
  if (status.solved) return { message: 'This one is already finished.', cells: [] };

  const intended = new Set(puzzle.solution);
  const wrongStars: number[] = [];
  const wrongCrosses: number[] = [];
  for (let cell = 0; cell < CELLS; cell += 1) {
    if (marks[cell] === STAR && !intended.has(cell)) wrongStars.push(cell);
    if (marks[cell] === DOT && intended.has(cell)) wrongCrosses.push(cell);
  }
  if (wrongStars.length > 0) {
    return {
      message:
        wrongStars.length === 1
          ? 'Careful — the highlighted star cannot be right, and everything worked out from it ' +
            'will go wrong too. Take it out and look again from there.'
          : `Careful — ${wrongStars.length} of your stars cannot be right. They are highlighted; ` +
            'take them out before going on.',
      cells: wrongStars,
    };
  }
  if (wrongCrosses.length > 0) {
    return {
      message:
        wrongCrosses.length === 1
          ? 'The highlighted square is crossed out, but it needs a star. Clear it before going on.'
          : `${wrongCrosses.length} squares you have crossed out need stars. They are ` +
            'highlighted; clear them before going on.',
      cells: wrongCrosses,
    };
  }

  const geo = buildGeometry(puzzle.regions);
  const deducer = new Deducer(geo);
  const alreadyKnown = new Uint8Array(CELLS);
  for (let cell = 0; cell < CELLS; cell += 1) {
    if (marks[cell] === STAR) deducer.place(cell);
    if (marks[cell] !== EMPTY) alreadyKnown[cell] = 1;
  }
  const next = deducer.nextDeduction(alreadyKnown);
  if (!next) {
    return {
      message:
        'Nothing more follows from the stars you have placed. Try a different corner, and look ' +
        'for a small region or a line with only a few open squares.',
      cells: [],
    };
  }

  const units = litUnits(next);
  const lit = new Set(units);
  const cells = [...new Set(units.flatMap((unit) => geo.units[unit] as number[]))];
  return { message: teach(next, lit), cells };
}

/**
 * Cells whose ✕ follows from a star already on the board.
 *
 * Used by "fill crosses", which puts in the marks the player would otherwise
 * place by hand: the eight cells around each star, and the rest of any unit that
 * already has both of its stars.
 */
export function impliedCrosses(puzzle: Puzzle, marks: readonly Mark[]): number[] {
  const { regions } = puzzle;
  const out = new Set<number>();

  const full = [0, 1, 2].map(() => new Array<number>(SIZE).fill(0));
  for (let cell = 0; cell < CELLS; cell += 1) {
    if (marks[cell] !== STAR) continue;
    [Math.floor(cell / SIZE), cell % SIZE, regions[cell] as number].forEach((index, family) => {
      const counts = full[family] as number[];
      counts[index] = (counts[index] as number) + 1;
    });
    for (const neighbor of NEIGHBORS[cell] as number[]) {
      if (marks[neighbor] === EMPTY) out.add(neighbor);
    }
  }

  for (let cell = 0; cell < CELLS; cell += 1) {
    if (marks[cell] !== EMPTY) continue;
    const indices = [Math.floor(cell / SIZE), cell % SIZE, regions[cell] as number];
    if (indices.some((index, family) => ((full[family] as number[])[index] as number) === STARS)) {
      out.add(cell);
    }
  }
  return [...out];
}
